import { useCallback } from 'react';

/**
 * useFlowViewVisibility Hook
 * 
 * Manages flow view visibility state and handlers.
 * 
 * UNIFIED VIEW MODEL:
 * All views are stored in sprint.views. The activeCustomView is always the
 * currently selected view. Visibility changes are saved to the view's settings
 * via useCustomViewSettings (which calls updateView).
 */
const useFlowViewVisibility = ({
  localFlowViewVisibility,
  setLocalFlowViewVisibility,
  defaultFlowVisibility,
  activeCustomView,
  updateView,
}) => {
  // Compute effective flow visibility:
  // - Use local state if set (during current session)
  // - Otherwise use stored view settings
  // - Fall back to defaults
  const storedVisibility = activeCustomView?.settings?.flowViewVisibility;
  const effectiveFlowViewVisibility = localFlowViewVisibility !== null 
    ? localFlowViewVisibility 
    : (storedVisibility || defaultFlowVisibility);

  // Handler for flow visibility changes
  // Updates local state immediately, and saves to view settings
  const handleFlowViewVisibilityChangeInternal = useCallback((newVisibility) => {
    // Update local state for immediate UI feedback
    setLocalFlowViewVisibility(newVisibility);
    
    // Save to view settings (if we have an active view)
    if (activeCustomView && updateView) {
      updateView(activeCustomView.id, {
        settings: {
          ...activeCustomView.settings,
          flowViewVisibility: newVisibility,
        },
      });
    }
  }, [activeCustomView, updateView, setLocalFlowViewVisibility]);

  return {
    effectiveFlowViewVisibility,
    handleFlowViewVisibilityChangeInternal,
  };
};

export default useFlowViewVisibility;
